<script setup lang="ts">
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import RlUpload from '@/components/common/upload/RlUpload.vue'
import { usePolicies } from '@/composables/asicw/policies'
import { NotifyFail } from '@/helpers/core/notifications'
import { downloadSampleBulkPolicyMinersFile } from '@/services/asicw/policies.service'
import { CoolingMethod, Environment, type IPolicyDetailsForm } from '@/types/asicw/policies'
import type { ILiteSiteFilters } from '@/types/core/filters'
import { downloadFile } from '@/util'
import { Icon } from '@iconify/vue'
import { type ElUpload } from 'element-plus'
import { computed, reactive, ref } from 'vue'

const form = defineModel<IPolicyDetailsForm>({ required: true })
const disableSalePriceField = ref(false)

// Need location and either:
// - number of policies or
// - addByCSV switch enabled and file present
const isFormValid = computed(
  () =>
    !!form.value.location?.site_id &&
    (!!form.value.numberOfPolicies || (!!form.value.addByCSV && !!form.value.minersCSV))
)

// Sites query to populate site options
const { getSiteOptionsQuery } = usePolicies()

const filters = reactive<ILiteSiteFilters>({})

const {
  data: sites,
  isError: isQueryError,
  error: queryError,
  isPending: isSitesQueryPending,
} = getSiteOptionsQuery(filters)

const siteOptions = computed(() =>
  sites.value?.data.map((site) => ({
    value: site,
    label: site.site_name,
  }))
)

const numberOfPoliciesCache = ref<number>()
const onCSVSwitchClick = () => {
  if (form.value.addByCSV) {
    numberOfPoliciesCache.value = form.value.numberOfPolicies
    form.value.numberOfPolicies = undefined
    disableSalePriceField.value = true
  } else {
    form.value.numberOfPolicies = numberOfPoliciesCache.value
    disableSalePriceField.value = false
  }
}

const elUploadRef = ref<InstanceType<typeof ElUpload>>()

const reset = () => {
  elUploadRef.value?.clearFiles()
}

const downloadSampleFile = async () => {
  try {
    await downloadFile(await downloadSampleBulkPolicyMinersFile(), 'bulk_policy_miners_sample.csv')
  } catch (err: any) {
    NotifyFail(`Failed to download sample file: ${err.message}`)
  }
}

const searchText = ref<string>()

const filterSites = (text?: string) => {
  searchText.value = text?.trim()
  filters.fragment = text && text.length ? text : undefined
}

defineExpose({ isFormValid, reset })
</script>

<template>
  <RlErrorAlert v-if="isQueryError" :error="queryError" />
  <el-form :label-width="192" label-position="left" class="px-4 w-full">
    <el-scrollbar height="460px" class="px-4 w-full">
      <el-form-item label="Location">
        <el-select-v2
          v-model="form.location"
          :options="siteOptions ?? []"
          remote
          :remote-method="filterSites"
          filterable
          clearable
          :loading="isSitesQueryPending"
          placeholder="Set location"
          value-key="site_id"
          class="w-full"
        />
      </el-form-item>
      <el-form-item label="Environment">
        <el-radio-group v-model="form.environment">
          <el-radio :value="Environment.ModularDataCenter">Modular Data Center</el-radio>
          <el-radio :value="Environment.StandaloneBuilding">Standalone Building</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="Cooling method">
        <el-radio-group v-model="form.coolingMethod">
          <el-radio :value="CoolingMethod.AirCooled">Air-cooled</el-radio>
          <el-radio :value="CoolingMethod.Immersion">Immersion</el-radio>
          <el-radio :value="CoolingMethod.Water">Water</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="Running 3rd party software">
        <el-radio-group v-model="form.running3rdPartySoft">
          <el-radio :value="false">No</el-radio>
          <el-radio :value="true">Yes</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="Overclocking miners">
        <el-radio-group v-model="form.overclockingMiners">
          <el-radio :value="false">No</el-radio>
          <el-radio :value="true">Yes</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="Number of policies">
        <el-input-number
          v-model="form.numberOfPolicies"
          :disabled="form.addByCSV"
          :min="0"
          :max="100"
          :placeholder="form.addByCSV ? '' : '0'"
        />
      </el-form-item>
      <el-form-item label="Add miners by CSV">
        <div class="flex gap-2 items-center w-full">
          <el-switch v-model="form.addByCSV" @click="onCSVSwitchClick" />
          <el-link v-if="form.addByCSV" size="small" type="primary" @click="downloadSampleFile"
            >Download sample CSV</el-link
          >
        </div>
      </el-form-item>
      <el-form-item v-if="form.addByCSV" label="Miner details CSV">
        <RlUpload
          v-if="!form.minersCSV"
          ref="elUploadRef"
          v-model="form.minersCSV"
          :acceptable-mime-types="['application/vnd.ms-excel', 'text/csv']"
        >
          <div class="el-upload__text px-8"><em>Upload a file</em> or drag and drop</div>
          <p class="text-text-tertiary">CSV up to 10MB</p>
        </RlUpload>
        <div v-else class="flex gap-2 items-center w-full px-2 py-1 hover:bg-component-fill-secondary">
          <Icon icon="mdi:file-csv" :height="20" />
          <span class="whitespace-nowrap text-ellipsis">{{ form.minersCSV.name }}</span>
          <Icon icon="mdi:close" class="ml-auto cursor-pointer" @click="form.minersCSV = undefined" />
        </div>
      </el-form-item>
    </el-scrollbar>
  </el-form>
</template>

<style scoped lang="scss">
.el-radio-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

:deep(.el-radio.is-checked) {
  .el-radio__label {
    color: var(--text-primary);
  }

  .el-upload-dragger {
    width: 100%;
  }
}
</style>
